/**
 * API Client for weatherapi.dev dashboard
 * Handles authenticated requests to the backend using Clerk tokens
 */

// Backend API base URL
// In unified dev mode (same origin), use empty string for relative URLs
// In production or separate servers, set PUBLIC_API_URL env var
const API_BASE_URL = (() => {
  // Check if we're in browser and can use relative URLs (same origin)
  if (typeof window !== "undefined") {
    // If PUBLIC_API_URL is set and inlined, use it
    // Otherwise use empty string for relative URLs (same origin)
    try {
      // @ts-ignore - This will be replaced at build time if PUBLIC_API_URL is set
      if (typeof process !== "undefined" && process.env?.PUBLIC_API_URL) {
        return process.env.PUBLIC_API_URL;
      }
    } catch {
      // process is not defined in browser, use relative URLs
    }
    return "";
  }
  // Server-side (shouldn't happen for this client, but just in case)
  return "https://weatherapi.isaiahballah.workers.dev";
})();

/**
 * API Error class for typed error handling
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Fetch with authentication using Clerk
 * @param endpoint - API endpoint (e.g., "/user/me")
 * @param getToken - Function to get Clerk session token
 * @param options - Fetch options
 */
export async function fetchWithAuth(
  endpoint: string,
  getToken: () => Promise<string | null>,
  options: RequestInit = {},
): Promise<Response> {
  const token = await getToken();

  if (!token) {
    throw new ApiError(
      401,
      "unauthenticated",
      "No authentication token available",
    );
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      errorData.error?.code || "unknown",
      errorData.error?.message ||
        `Request failed with status ${response.status}`,
    );
  }

  return response;
}

// ============================================================================
// API Types
// ============================================================================

export interface ApiKey {
  id: string;
  key_prefix: string;
  name: string;
  environment: "live" | "test";
  status: "active" | "revoked";
  created_at: string;
  last_used_at: string | null;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  company?: string | null;
  timezone?: string;
  plan: "free" | "developer" | "pro" | "scale" | "enterprise";
  created_at: string;
}

export interface UsageStats {
  current_month: number;
  plan_limit: number;
}

export interface UserMeResponse {
  success: boolean;
  data: {
    user: User;
    api_keys: ApiKey[];
    usage: UsageStats;
  };
}

export interface CreateApiKeyResponse {
  success: boolean;
  data: {
    api_key: ApiKey & { key: string }; // Full key only shown once
    message: string;
  };
}

export interface UpdateSettingsRequest {
  name?: string;
  company?: string;
  timezone?: string;
}

export interface UpdateSettingsResponse {
  success: boolean;
  data: {
    user: User;
    message: string;
  };
}

export interface BasicUsageResponse {
  success: boolean;
  data: {
    period: {
      start: string;
      end: string;
    };
    total_requests: number;
    plan_limit: number;
    remaining: number;
    cache_hit_rate: number;
    avg_latency_ms: number;
    error_rate: number;
    endpoints: Record<string, number>;
    status_codes: Record<number, number>;
  };
}

export interface DailyUsageData {
  date: string;
  requests: number;
  cached: number;
  errors: number;
}

export interface EndpointStats {
  endpoint: string;
  requests: number;
  percentage: number;
}

export interface DetailedUsageResponse {
  success: boolean;
  data: {
    period: {
      start: string;
      end: string;
      days: number;
    };
    summary: {
      total_requests: number;
      cached_requests: number;
      cache_hit_rate: number;
      avg_latency_ms: number;
      error_rate: number;
      error_count: number;
    };
    quota: {
      used: number;
      limit: number;
      remaining: number;
      percentage: number;
    };
    daily: DailyUsageData[];
    endpoints: EndpointStats[];
    status_codes: Record<number, number>;
  };
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get current user info, API keys, and usage stats
 */
export async function getUserMe(
  getToken: () => Promise<string | null>,
): Promise<UserMeResponse["data"]> {
  const response = await fetchWithAuth("/user/me", getToken);
  const json: UserMeResponse = await response.json();
  return json.data;
}

/**
 * Update user settings
 */
export async function updateUserSettings(
  getToken: () => Promise<string | null>,
  settings: UpdateSettingsRequest,
): Promise<UpdateSettingsResponse["data"]> {
  const response = await fetchWithAuth("/user/settings", getToken, {
    method: "PUT",
    body: JSON.stringify(settings),
  });
  const json: UpdateSettingsResponse = await response.json();
  return json.data;
}

/**
 * Get basic usage statistics for current month
 */
export async function getUserUsage(
  getToken: () => Promise<string | null>,
): Promise<BasicUsageResponse["data"]> {
  const response = await fetchWithAuth("/user/usage", getToken);
  const json: BasicUsageResponse = await response.json();
  return json.data;
}

/**
 * Get detailed usage analytics with daily breakdown
 * @param days - Number of days to fetch (1-90, default 7)
 */
export async function getUserUsageDetailed(
  getToken: () => Promise<string | null>,
  days: number = 7,
): Promise<DetailedUsageResponse["data"]> {
  const response = await fetchWithAuth(
    `/user/usage/detailed?days=${days}`,
    getToken,
  );
  const json: DetailedUsageResponse = await response.json();
  return json.data;
}

/**
 * Create a new API key
 */
export async function createApiKey(
  getToken: () => Promise<string | null>,
  name: string,
  environment: "live" | "test",
): Promise<CreateApiKeyResponse["data"]> {
  const response = await fetchWithAuth("/user/api-keys", getToken, {
    method: "POST",
    body: JSON.stringify({ name, environment }),
  });
  const json: CreateApiKeyResponse = await response.json();
  return json.data;
}

/**
 * Revoke an API key
 */
export async function revokeApiKey(
  getToken: () => Promise<string | null>,
  keyId: string,
): Promise<void> {
  await fetchWithAuth(`/user/api-keys/${keyId}`, getToken, {
    method: "DELETE",
  });
}

/**
 * Get the current API base URL (useful for debugging)
 */
export function getApiBaseUrl(): string {
  return API_BASE_URL;
}
